import React, { useState, useEffect } from 'react';
import FooterSection from '../components/FooterSection'; // adjust path if needed
import axios from "axios";
import config from '../config/config';

const FooterAdmin = ({ slug = '', onSave }) => {
  // Default states (used only if no DB data)
  const defaultState = {
    preset: 'comprehensive',
    brandName: 'BRAND',
    brandDesc: 'Premium quality meets modern design. Your destination for timeless style.',
    newsletterTitle: 'Stay in the Loop',
    newsletterDesc: 'Subscribe to get special offers, free giveaways, and exclusive deals.',
    ctaText: 'Subscribe',
    socialLinks: ['Facebook', 'Instagram', 'Twitter', 'Youtube'],
    shopLinks: ['New Arrivals', 'Bestsellers', 'Sale', 'Collections', 'Gift Cards'],
    serviceLinks: ['Contact Us', 'Shipping Info', 'Returns', 'FAQ', 'Size Guide'],
    companyLinks: ['About Us', 'Careers', 'Press', 'Sustainability', 'Affiliates'],
    contact: {
      email: 'support@brand.com',
      phone: '+1 (555) 123-4567',
      address: '123 Fashion St, New York, NY 10001',
    },
    trustBadges: ['Secure Payment', 'Free Shipping'],
    paymentIcons: ['Visa', 'MasterCard', 'Amex'],
    bottomText: '© 2025 Brand. All rights reserved.',
    bottomLegalLinks: ['Privacy Policy', 'Terms of Service', 'Cookies'],
    devMode: false,
    container: {
      padding: 'py-12 md:py-16',
      background: 'bg-gray-900',
      textColor: '#ffffff',
      linkColor: '#9ca3af',
      linkHover: '#ffffff',
      newsletterBg: 'bg-gray-800',
    },
    columns: {
      mobileCols: 2,
      desktopCols: 5,
    },
  };

  // All states initialized with defaults
  const [preset, setPreset] = useState(defaultState.preset);
  const [brandName, setBrandName] = useState(defaultState.brandName);
  const [brandDesc, setBrandDesc] = useState(defaultState.brandDesc);
  const [newsletterTitle, setNewsletterTitle] = useState(defaultState.newsletterTitle);
  const [newsletterDesc, setNewsletterDesc] = useState(defaultState.newsletterDesc);
  const [ctaText, setCtaText] = useState(defaultState.ctaText);
  const [socialLinks, setSocialLinks] = useState(defaultState.socialLinks);
  const [shopLinks, setShopLinks] = useState(defaultState.shopLinks);
  const [serviceLinks, setServiceLinks] = useState(defaultState.serviceLinks);
  const [companyLinks, setCompanyLinks] = useState(defaultState.companyLinks);
  const [contact, setContact] = useState(defaultState.contact);
  const [trustBadges, setTrustBadges] = useState(defaultState.trustBadges);
  const [paymentIcons, setPaymentIcons] = useState(defaultState.paymentIcons);
  const [bottomText, setBottomText] = useState(defaultState.bottomText);
  const [bottomLegalLinks, setBottomLegalLinks] = useState(defaultState.bottomLegalLinks);
  const [devMode, setDevMode] = useState(defaultState.devMode);
  const [container, setContainer] = useState(defaultState.container);
  const [columns, setColumns] = useState(defaultState.columns);

  const [fullScreenPreview, setFullScreenPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");

  // Load settings from DB
  useEffect(() => {
    const loadSettings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');

        if (isDuplicate) {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            `${config.API_URL}/api/pullFooterSettings`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate 
          ? (response.data.customSettings || {}) 
          : (response.data.footerSettings || {});

        // Apply DB values ONLY if they exist — this overrides defaults
        if (dbData.preset) setPreset(dbData.preset);
        if (dbData.brandName) setBrandName(dbData.brandName);
        if (dbData.brandDesc) setBrandDesc(dbData.brandDesc);
        if (dbData.newsletterTitle) setNewsletterTitle(dbData.newsletterTitle);
        if (dbData.newsletterDesc) setNewsletterDesc(dbData.newsletterDesc);
        if (dbData.ctaText) setCtaText(dbData.ctaText);

        if (dbData.socialLinks) setSocialLinks(dbData.socialLinks);
        if (dbData.shopLinks) setShopLinks(dbData.shopLinks);
        if (dbData.serviceLinks) setServiceLinks(dbData.serviceLinks);
        if (dbData.companyLinks) setCompanyLinks(dbData.companyLinks);

        if (dbData.contact) setContact(dbData.contact);

        if (dbData.trustBadges) setTrustBadges(dbData.trustBadges);
        if (dbData.paymentIcons) setPaymentIcons(dbData.paymentIcons);
        if (dbData.bottomText) setBottomText(dbData.bottomText);
        if (dbData.bottomLegalLinks) setBottomLegalLinks(dbData.bottomLegalLinks);

        if (typeof dbData.devMode === 'boolean') setDevMode(dbData.devMode);
        if (dbData.customStyles?.container) setContainer(dbData.customStyles.container);
        if (dbData.columns) setColumns(dbData.columns);
      } catch (error) {
        console.error("Failed to load footer settings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [token, slug]);

  const currentConfig = {
    preset,
    brandName,
    brandDesc,
    newsletterTitle,
    newsletterDesc,
    ctaText,
    socialLinks,
    shopLinks,
    serviceLinks,
    companyLinks,
    contact,
    trustBadges,
    paymentIcons,
    bottomText,
    bottomLegalLinks,
    customStyles: { container },
    columns,
    devMode,
  };

  const saveSettings = async () => {
    if (!token) {
      alert("Please log in first!");
      return;
    }

    try {
      setSaving(true);
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const endpoint = slug ? `/api/saveFooterSettings/${slug}` : '/api/saveFooterSettings';

      const response = await axios.post(
        `${config.API_URL}${endpoint}`,
        { settings: currentConfig },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert("Footer saved successfully! 🎉");
        if (onSave) onSave(currentConfig);
      } else {
        alert("Save failed.");
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  // Array helpers
  const addItem = (arr, setArr, defaultVal = 'New Item') => setArr([...arr, defaultVal]);
  const updateItem = (arr, setArr, index, value) => {
    const newArr = [...arr];
    newArr[index] = value;
    setArr(newArr);
  };
  const removeItem = (arr, setArr, index) => setArr(arr.filter((_, i) => i !== index));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading your saved footer config...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Preview */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900">Footer Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
            >
              Full Screen Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gray-50 p-8">
            <FooterSection config={currentConfig} />
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      <div className="w-96 bg-white border-l overflow-y-auto shadow-2xl">
        <div className="p-6 space-y-6 pb-40">
          {/* Preset */}
          <div>
            <h3 className="font-medium mb-3">Layout Style</h3>
            <div className="grid gap-3">
              {['comprehensive', 'minimal-clean', 'compact-cta'].map(p => (
                <button
                  key={p}
                  onClick={() => setPreset(p)}
                  className={`p-4 border rounded-lg text-left ${preset === p ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                >
                  <span className="font-medium capitalize">{p.replace('-', ' ')}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Brand */}
          <div className="space-y-3">
            <input value={brandName} onChange={e => setBrandName(e.target.value)} className="w-full px-4 py-2 border rounded-md" placeholder="Brand Name" />
            <textarea value={brandDesc} onChange={e => setBrandDesc(e.target.value)} className="w-full px-4 py-2 border rounded-md h-20" placeholder="Brand Description" />
          </div>

          {/* Newsletter */}
          <div className="space-y-3">
            <input value={newsletterTitle} onChange={e => setNewsletterTitle(e.target.value)} className="w-full px-4 py-2 border rounded-md" placeholder="Newsletter Title" />
            <textarea value={newsletterDesc} onChange={e => setNewsletterDesc(e.target.value)} className="w-full px-4 py-2 border rounded-md h-20" placeholder="Newsletter Description" />
            <input value={ctaText} onChange={e => setCtaText(e.target.value)} className="w-full px-4 py-2 border rounded-md" placeholder="CTA Button Text" />
          </div>

          {/* Editable Arrays */}
          <div className="space-y-6">
            {[
              { title: "Shop Links", arr: shopLinks, set: setShopLinks },
              { title: "Service Links", arr: serviceLinks, set: setServiceLinks },
              { title: "Company Links", arr: companyLinks, set: setCompanyLinks },
              { title: "Social Platforms", arr: socialLinks, set: setSocialLinks },
              { title: "Trust Badges", arr: trustBadges, set: setTrustBadges },
              { title: "Payment Icons", arr: paymentIcons, set: setPaymentIcons },
              { title: "Bottom Legal Links", arr: bottomLegalLinks, set: setBottomLegalLinks },
            ].map(({ title, arr, set }) => (
              <div key={title}>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium">{title}</h3>
                  <button onClick={() => addItem(arr, set)} className="text-blue-600 text-sm hover:underline">+ Add</button>
                </div>
                {arr.map((item, i) => (
                  <div key={i} className="flex gap-2 mt-2">
                    <input
                      value={item}
                      onChange={e => updateItem(arr, set, i, e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-md"
                    />
                    <button onClick={() => removeItem(arr, set, i)} className="text-red-600 px-3 hover:bg-red-50 rounded">×</button>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Bottom Copyright */}
          <div className="space-y-3">
            <h3 className="font-medium">Bottom Copyright Text</h3>
            <input
              value={bottomText}
              onChange={e => setBottomText(e.target.value)}
              className="w-full px-4 py-2 border rounded-md"
              placeholder="© 2025 Your Brand. All rights reserved."
            />
          </div>

          {/* Contact */}
          <div className="space-y-3">
            <h3 className="font-medium">Contact Info</h3>
            <input value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} className="w-full px-4 py-2 border rounded-md" placeholder="Email" />
            <input value={contact.phone} onChange={e => setContact({ ...contact, phone: e.target.value })} className="w-full px-4 py-2 border rounded-md" placeholder="Phone" />
            <input value={contact.address} onChange={e => setContact({ ...contact, address: e.target.value })} className="w-full px-4 py-2 border rounded-md" placeholder="Address" />
          </div>

          {/* Columns */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Mobile Columns</label>
              <select value={columns.mobileCols} onChange={e => setColumns({ ...columns, mobileCols: Number(e.target.value) })} className="w-full border rounded-md px-3 py-2">
                {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Desktop Columns</label>
              <select value={columns.desktopCols} onChange={e => setColumns({ ...columns, desktopCols: Number(e.target.value) })} className="w-full border rounded-md px-3 py-2">
                {[3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {/* Dev Mode */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-yellow-900">Dev Mode – Custom Styles</h3>
              <button
                onClick={() => setDevMode(!devMode)}
                className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${devMode ? 'bg-yellow-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition ${devMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {devMode && (
              <div className="space-y-4 text-sm">
                <div>
                  <label className="block font-medium mb-1">Padding (Tailwind)</label>
                  <input value={container.padding} onChange={e => setContainer({ ...container, padding: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block font-medium mb-1">Background</label>
                  <input value={container.background} onChange={e => setContainer({ ...container, background: e.target.value })} className="w-full px-3 py-2 border rounded-md" placeholder="bg-gray-900 or url(...)" />
                </div>
                <div>
                  <label className="block font-medium mb-1">Text Color</label>
                  <input value={container.textColor} onChange={e => setContainer({ ...container, textColor: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block font-medium mb-1">Link Color</label>
                  <input value={container.linkColor} onChange={e => setContainer({ ...container, linkColor: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block font-medium mb-1">Link Hover Color</label>
                  <input value={container.linkHover} onChange={e => setContainer({ ...container, linkHover: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block font-medium mb-1">Newsletter/CTA Bg</label>
                  <input value={container.newsletterBg} onChange={e => setContainer({ ...container, newsletterBg: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Save Button */}
        <div className="fixed bottom-0 right-0 w-96 bg-white border-t p-6 shadow-lg">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save & Publish'}
          </button>
        </div>
      </div>

      {/* Fullscreen Preview */}
      {fullScreenPreview && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" onClick={() => setFullScreenPreview(false)}>
          <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-end">
            <button onClick={() => setFullScreenPreview(false)} className="text-white text-5xl hover:text-gray-300">×</button>
          </div>
          <div className="flex-1 overflow-auto p-8" onClick={e => e.stopPropagation()}>
            <FooterSection config={currentConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterAdmin;